import { useState, useEffect } from 'react';
import debounce from '../utils/debounce';

/**
 * This hook determines if the current screen width exceeds a specified breakpoint.
 * - It listen for window resize events to update the screen size status dynamically
 * - Includes a debounce of 100ms to avoid performance issues with frequents resize events
 * @param {*} breakpoint - The screen width threshold for determining "large screen" (default: 768px)
 * @returns {boolean} - Returns true if the screen width is larger than the breakpoint.
 */
const useIsScreenAboveBreakpoint = (breakpoint = 768) => {
  const [isLargeScreen, setIsLargeScreen] = useState(window.innerWidth > breakpoint);

  useEffect(() => {
    let timeoutId = null;

    const handleResize = debounce(() => {
      const currentScreen = window.innerWidth > breakpoint;
      if (currentScreen !== isLargeScreen) {
        setIsLargeScreen(currentScreen);
      }
    }, 100);

    window.addEventListener('resize', handleResize);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', handleResize);
    };
  }, [breakpoint, isLargeScreen]);

  return isLargeScreen;
};

export default useIsScreenAboveBreakpoint;
